/**
 * DesktopLayout - Multi-panel container for desktop view
 *
 * Layout structure:
 * - Left sidebar: Navigation (64px collapsed, 240px expanded)
 * - Main content: Portfolio, chart, stats
 * - Right panel: Multi-chart, achievements (1440px+)
 */

import React from 'react';
import { motion } from 'framer-motion';
import DesktopSidebar from './DesktopSidebar';
import { useDesktopView } from '../../../hooks/useDesktopView';
import { useSidebar } from '../../../contexts/SidebarContext';

interface DesktopLayoutProps {
  children: React.ReactNode;
  rightPanel?: React.ReactNode;
}

const DesktopLayout: React.FC<DesktopLayoutProps> = ({ children, rightPanel }) => {
  const { showRightPanel } = useDesktopView();
  const { isExpanded: sidebarExpanded, toggle: toggleSidebar } = useSidebar();

  return (
    <div className="flex min-h-screen bg-black text-white">
      {/* Left Sidebar */}
      <DesktopSidebar
        isExpanded={sidebarExpanded}
        onToggle={toggleSidebar}
      />

      {/* Main Content Area */}
      <motion.main
        className="flex-1 min-w-0 overflow-y-auto"
        layout
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      >
        <div className="max-w-5xl mx-auto px-6 py-6">
          {children}
        </div>
      </motion.main>

      {/* Right Panel (1440px+) */}
      {showRightPanel && rightPanel && (
        <motion.aside
          className="w-right-panel border-l border-gray-800/50 bg-gray-900/30 overflow-y-auto"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        >
          <div className="p-4">
            {rightPanel}
          </div>
        </motion.aside>
      )}
    </div>
  );
};

export default DesktopLayout;
